<template>
  <v-container fluid class="pa-16 pt-8">
    <v-form ref="form" v-model="valid">
      <v-row class="align-center">
        <v-col cols="12" md="9" class="pb-0">
          <h2 class="form-label">Communication Address</h2>
        </v-col>
        <v-col cols="12" md="3" v-if="allForm.residence === 'false'">
          <v-checkbox
            v-model="form.current_address.is_indian_address"
            class="mt-1"
            density="compact"
            :readonly="isReadOnly"
            @change="changeCountryName('current_address')"
            hide-details="auto"
          >
            <template v-slot:label>
              <span>Is Indian Address</span>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Address Line 1 <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.current_address.address_line1"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Address Line 2</label>
          <v-text-field
            v-model="form.current_address.address_line2"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Country <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.current_address.country"
            hide-details="auto"
            outlined
            dense
            :items="countries"
            item-text="name"
            item-value="name"
            :readonly="form.current_address.is_indian_address || isReadOnly"
            :rules="[rules.required]"
            :append-icon="
              form.current_address.is_indian_address ? '' : '$dropdown'
            "
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >{{
              form.current_address.country === "India"
                ? "State"
                : "State/Province"
            }}
            <span class="error--text">* </span></label
          >
          <v-select
            v-if="form.current_address.country === 'India'"
            v-model="form.current_address.state"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :items="statesList"
            :rules="[rules.required]"
            attach
          ></v-select>
          <v-text-field
            v-else
            v-model="form.current_address.state"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >{{
              form.current_address.country === "India" ? "City" : "City/Town"
            }}
            <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.current_address.city"
            hide-details="auto"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Postcode <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.current_address.postcode"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[
              rules.required,
              form.current_address.country === 'India' ? rules.number : true,
              form.current_address.country === 'India'
                ? (v) => v.length === 6 || 'Enter a valid pincode'
                : true,
            ]"
            :maxlength="form.current_address.country === 'India' ? 6 : null"
            hide-details="auto"
            type="email"
            @change="
              form.current_address.postcode =
                form.current_address.postcode.toUpperCase()
            "
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row v-if="form.current_address.is_indian_address">
        <v-col cols="3" md="1" class="pt-4 pb-0">
          <label class="form-label"
            >Locality <span class="error--text">* </span></label
          >
        </v-col>
        <v-col cols="9" class="input-col pb-0">
          <v-radio-group
            v-model="form.current_address.locality"
            row
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
            class="pt-0"
            hide-details="auto"
          >
            <v-radio label="Urban" value="urban"></v-radio>
            <v-radio label="Rural" value="rural"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row class="form-row align-center">
        <v-col cols="7" md="4" class="pt-4 pb-0">
          <label class="form-label">
            Is communication address same as permanent address?
            <span class="error--text">* </span>
          </label>
        </v-col>
        <v-col cols="5" md="3">
          <v-radio-group
            v-model="form.is_permanent_address_same"
            row
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
            :disabled="!isCurrentAddressComplete"
            hide-details="auto"
          >
            <v-radio label="Yes" value="true"></v-radio>
            <v-radio label="No" value="false"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row
        class="align-center"
        v-if="form.is_permanent_address_same == 'false'"
      >
        <v-col cols="12" md="9" class="pb-0">
          <h2 class="form-label">Permanent Address</h2>
        </v-col>
        <v-col cols="12" md="3" v-if="allForm.residence === 'false'">
          <v-checkbox
            v-model="form.permanent_address.is_indian_address"
            class="mt-1"
            density="compact"
            :readonly="isReadOnly"
            @change="changeCountryName('permanent_address')"
            hide-details="auto"
          >
            <template v-slot:label>
              <span>Is Indian Address</span>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>
      <v-row v-if="form.is_permanent_address_same == 'false'">
        <v-col cols="12" md="4">
          <label class="form-label"
            >Address Line 1 <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.permanent_address.address_line1"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Address Line 2</label>
          <v-text-field
            v-model="form.permanent_address.address_line2"
            outlined
            dense
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Country <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.permanent_address.country"
            hide-details="auto"
            outlined
            dense
            :readonly="isReadOnly"
            :items="countries"
            item-text="name"
            item-value="name"
            :rules="[rules.required]"
            attach
            :menu-props="{ top: true, offsetY: true }"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >{{
              form.permanent_address.country === "India"
                ? "State"
                : "State/Province"
            }}
            <span class="error--text">* </span></label
          >
          <v-select
            v-if="form.permanent_address.country === 'India'"
            v-model="form.permanent_address.state"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :items="statesList"
            :rules="[rules.required]"
            attach
          ></v-select>
          <v-text-field
            v-else
            v-model="form.permanent_address.state"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >{{
              form.permanent_address.country === "India" ? "City" : "City/Town"
            }}
            <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.permanent_address.city"
            hide-details="auto"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Postcode <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.permanent_address.postcode"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[
              rules.required,
              form.permanent_address.country === 'India' ? rules.number : true,
              form.permanent_address.country === 'India'
                ? (v) => v.length === 6 || 'Enter a valid pincode'
                : true,
            ]"
            :maxlength="form.permanent_address.country === 'India' ? 6 : null"
            hide-details="auto"
            type="email"
            @change="
              form.permanent_address.postcode =
                form.permanent_address.postcode.toUpperCase()
            "
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row
        v-if="
          form.is_permanent_address_same == 'false' &&
          form.permanent_address.is_indian_address
        "
      >
        <v-col cols="3" md="1" class="pt-4">
          <label class="form-label"
            >Locality <span class="error--text">* </span></label
          >
        </v-col>
        <v-col cols="9" class="input-col">
          <v-radio-group
            v-model="form.permanent_address.locality"
            row
            dense
            :rules="[rules.required]"
            class="pt-0"
            :readonly="isReadOnly"
            hide-details="auto"
          >
            <v-radio label="Urban" value="urban"></v-radio>
            <v-radio label="Rural" value="rural"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row v-if="!isReadOnly && !isAdmitted" class="form-row mt-6">
        <v-col cols="12" class="d-flex justify-end pr-0">
          <v-btn
            :disabled="JSON.stringify(this.value) === JSON.stringify(this.form)"
            color="success"
            class="mr-3"
            @click="submit"
          >
            Save & Next
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import states from "@/assets/states.json";

export default {
  components: {},
  data: () => ({
    rules,
    menu: false,
    valid: false,
    today: new Date().toISOString().substr(0, 10),
    form: {},
    countries: [],
    states,
  }),
  props: {
    value: {
      type: Object,
      required: true,
    },
    allForm: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: () => true,
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.form = JSON.parse(JSON.stringify(newVal));
        this.$emit("validate", true);
      },
      deep: true,
    },
    // "form.is_permanent_address_same"(newVal) {
    //   if (newVal === "true") {
    //     this.form.permanent_address = { ...this.form.current_address };
    //   } else {
    //     this.form.permanent_address = {
    //       is_indian_address: true,
    //       address_line1: "",
    //       address_line2: "",
    //       city: "",
    //       state: "",
    //       postcode: "",
    //       locality: "",
    //       country: "",
    //     };
    //   }
    // },
    valid(newValue) {
      if (
        !newValue ||
        JSON.stringify(this.value) !== JSON.stringify(this.form)
      ) {
        this.$emit("validate", false);
      } else {
        this.$emit("validate", true);
      }
    },
    form: {
      handler(newVal) {
        if (
          !this.valid ||
          JSON.stringify(this.value) !== JSON.stringify(newVal)
        ) {
          this.$emit("validate", false);
        } else {
          this.$emit("validate", true);
        }
      },
      deep: true,
    },
  },
  async created() {
    this.form = JSON.parse(JSON.stringify(this.value));
    await this.getCountries();
    this.changeCountryName("current_address");
  },
  methods: {
    changeCountryName(address) {
      if (this.form[address].is_indian_address) {
        this.form[address].country = this.countries.find(
          (item) => item.name == "India"
        )?.name;
      }
    },
    async getCountries() {
      try {
        const response = await this.$axios.get("/countries");
        this.countries = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    submit() {
      if (!this.$refs.form.validate()) {
        return;
      }
      if (this.form.is_permanent_address_same === "true") {
        this.form.permanent_address = { ...this.form.current_address };
      }
      this.$emit("submit", this.form);
    },
  },
  computed: {
    isAdmitted() {
      return (
        (this.allForm.application_status || "").toString().toLowerCase() ===
        "admitted"
      );
    },
    statesList() {
      return this.states.map((state) => state.name);
    },
    isCurrentAddressComplete() {
      const {
        address_line1,
        state,
        city,
        postcode,
        locality,
        is_indian_address,
      } = this.form.current_address;
      if (is_indian_address) {
        return address_line1 && state && city && postcode && locality;
      } else {
        return address_line1 && state && city && postcode;
      }
    },
  },
};
</script>

<style scoped>
.input-col {
  padding-top: 0; /* Ensure inputs align properly */
}

.form-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
}
</style>
